import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './ApplicationSuccess.css';

type SuccessLocationState = {
  loanNumber?: string;
  submittedAt?: string;
};

const TIMELINE_STEPS = [
  {
    title: 'Document Verification',
    description: "We'll review your NSFAS and student documents (within 24 hours).",
    status: 'in_progress' as const,
    icon: 'document',
  },
  {
    title: 'Application Review',
    description: "We'll review your documents and application details (within 24 hours).",
    status: 'pending' as const,
    icon: 'review',
  },
  {
    title: 'Loan Approval',
    description: "You'll receive SMS and email notification of your approval status.",
    status: 'pending' as const,
    icon: 'approval',
  },
  {
    title: 'Fund Transfer',
    description: 'Approved funds transferred to your bank account (2–4 hours).',
    status: 'pending' as const,
    icon: 'transfer',
  },
];

function formatSubmittedAt(iso?: string): string {
  const date = iso ? new Date(iso) : new Date();
  const datePart = date.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('en-ZA', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart} • ${timePart}`;
}

function buildReference(loanNumber?: string, firstName?: string): string {
  if (loanNumber) return loanNumber;
  const initials = (firstName || 'GL').slice(0, 2).toUpperCase();
  const stamp = Date.now().toString().slice(-6);
  return `GL${stamp}-${initials}001`;
}

const TimelineIcon: React.FC<{ type: string }> = ({ type }) => {
  if (type === 'document') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14,2 14,8 20,8" />
      </svg>
    );
  }
  if (type === 'review') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
    );
  }
  if (type === 'approval') {
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 2 11 13" />
        <path d="M22 2 15 22 11 13 2 9 22 2" />
      </svg>
    );
  }
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 21h18" />
      <path d="M5 21V7l7-4 7 4v14" />
      <path d="M9 21v-6h6v6" />
    </svg>
  );
};

const ApplicationSuccess: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const state = (location.state as SuccessLocationState) || {};

  const reference = useMemo(
    () => buildReference(state.loanNumber, user?.first_name),
    [state.loanNumber, user?.first_name]
  );
  const submittedAt = formatSubmittedAt(state.submittedAt);
  const email = user?.email || 'your registered email';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="application-success">
      <div className="success-card">
        <header className="success-hero">
          <div className="success-hero__pattern" aria-hidden="true" />
          <div className="success-hero__check">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M9 12l2 2 4-4"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="success-hero__title">Application Submitted Successfully!</h1>
          <p className="success-hero__subtitle">
            Thank you, {user?.first_name}! Your loan application has been received and is now being
            processed.
          </p>
        </header>

        <div className="success-email-notice">
          <div className="success-email-notice__icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <div>
            <p className="success-email-notice__main">
              We&apos;ve sent a confirmation email to <strong>{email}</strong>
            </p>
            <p className="success-email-notice__sub">
              You&apos;ll also receive SMS updates as your application progresses.
            </p>
          </div>
        </div>

        <section className="success-timeline-section">
          <h2 className="success-section-title">What happens next?</h2>
          <ol className="success-timeline">
            {TIMELINE_STEPS.map((step, index) => (
              <li
                key={step.title}
                className={`success-timeline__item${
                  step.status === 'in_progress' ? ' success-timeline__item--active' : ''
                }`}
              >
                <div className="success-timeline__step">{index + 1}</div>
                <div className="success-timeline__icon">
                  <TimelineIcon type={step.icon} />
                </div>
                <div className="success-timeline__content">
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
                <span
                  className={`success-timeline__badge success-timeline__badge--${step.status}`}
                >
                  {step.status === 'in_progress' ? 'In progress' : 'Pending'}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <div className="success-meta">
          <div className="success-meta__card">
            <div className="success-meta__icon success-meta__icon--ref">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
              </svg>
            </div>
            <div>
              <span className="success-meta__label">Application Reference</span>
              <div className="success-meta__value success-meta__value--ref">{reference}</div>
            </div>
          </div>

          <div className="success-meta__card">
            <div className="success-meta__icon success-meta__icon--date">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div>
              <span className="success-meta__label">Submitted On</span>
              <div className="success-meta__value">{submittedAt}</div>
            </div>
          </div>
        </div>

        <div className="success-info-cards">
          <div className="success-info-card">
            <div className="success-info-card__icon success-info-card__icon--notify">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                <path d="M13.73 21a2 2 0 0 1-3.46 0" />
              </svg>
            </div>
            <h3>Stay Updated</h3>
            <p>We&apos;ll send you SMS and email updates at every step of the process</p>
          </div>

          <div className="success-info-card">
            <div className="success-info-card__icon success-info-card__icon--time">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
            </div>
            <h3>Processing Time</h3>
            <p>Most applications are processed within 24 hours during business days</p>
          </div>

          <div className="success-info-card">
            <div className="success-info-card__icon success-info-card__icon--payout">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2l10 5-10 5L2 7l10-5z" />
                <path d="M2 17l10 5 10-5" />
                <path d="M2 12l10 5 10-5" />
              </svg>
            </div>
            <h3>Quick Payout</h3>
            <p>Approved funds typically arrive in 2-4 hours during banking hours</p>
          </div>
        </div>

        <footer className="success-actions">
          <button
            type="button"
            className="success-actions__secondary"
            onClick={() => navigate('/dashboard')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9,22 9,12 15,12 15,22" />
            </svg>
            Go to Dashboard
          </button>
          <button
            type="button"
            className="success-actions__primary"
            onClick={() => navigate('/dashboard/history')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14,2 14,8 20,8" />
            </svg>
            View Loan History
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ApplicationSuccess;
